/**
 * LIFF Service
 * Handles LINE Front-end Framework integration
 */

import liff from '@line/liff';
import { getLiffId } from './config';
import type { UserProfile } from './types';

// Store user profile
let userProfile: UserProfile | null = null;

// Demo mode flag
let isDemoMode = false;

/**
 * Check if in demo mode
 */
export function isDemo(): boolean {
  return isDemoMode;
}

/**
 * Initialize LIFF SDK
 * Resolves LIFF ID at runtime from tenant config API (SaaS-ready).
 */
export async function initLiff(): Promise<void> {
  // Resolve LIFF ID dynamically (API → env fallback)
  const liffId = await getLiffId();

  if (!liffId || liffId === 'your-liff-id-here' || liffId.length < 10) {
    console.warn('LIFF ID not configured, running in demo mode');
    isDemoMode = true;
    return;
  }

  try {
    await liff.init({ liffId });
    console.log('LIFF initialized successfully (liffId:', liffId, ')');
  } catch (error) {
    console.warn('LIFF initialization failed, falling back to demo mode:', error);
    isDemoMode = true;
  }
}

/**
 * Check if running in LINE app
 */
export function isInLineApp(): boolean {
  if (isDemoMode) return false;
  return liff.isInClient();
}

/**
 * Check if user is logged in
 */
export function isLoggedIn(): boolean {
  if (isDemoMode) return true;
  return liff.isLoggedIn();
}

/**
 * Login user
 */
export function login(): void {
  if (isDemoMode) return;
  if (!isLoggedIn()) {
    liff.login();
  }
}

/**
 * Logout user
 */
export function logout(): void {
  if (isDemoMode) return;
  if (isLoggedIn()) {
    liff.logout();
  }
}

/**
 * Get user profile
 */
export async function getUserProfile(): Promise<UserProfile> {
  // Demo mode - return fake profile with consistent ID
  if (isDemoMode) {
    return {
      userId: 'demo-user-001',
      displayName: 'Demo User',
      language: navigator.language || 'th',
    };
  }

  if (userProfile) {
    return userProfile;
  }

  try {
    const profile = await liff.getProfile();
    const language = liff.getLanguage() || 'th';

    userProfile = {
      userId: profile.userId,
      displayName: profile.displayName,
      pictureUrl: profile.pictureUrl,
      language,
    };

    return userProfile;
  } catch (error) {
    console.error('Failed to get user profile:', error);
    throw new Error('Failed to get user profile');
  }
}

/**
 * Alias for getUserProfile (for consistency)
 */
export const getProfile = getUserProfile;

/**
 * Get LIFF language setting
 */
export function getLiffLanguage(): string {
  if (isDemoMode) return navigator.language || 'th';
  return liff.getLanguage() || 'th';
}

/**
 * Close LIFF window (return to LINE chat)
 */
export function closeWindow(): void {
  if (isInLineApp()) {
    liff.closeWindow();
  } else {
    window.close();
  }
}

/**
 * Send message to LINE chat
 */
export async function sendMessage(message: string | Record<string, any>): Promise<void> {
  console.log('[LIFF] sendMessage called');
  console.log('[LIFF] isDemoMode:', isDemoMode);
  console.log('[LIFF] isInLineApp:', isInLineApp());
  console.log('[LIFF] Message:', message);

  if (isDemoMode) {
    console.warn('[Demo] Cannot send message in demo mode');
    const displayText = typeof message === 'string' ? message : JSON.stringify(message, null, 2);
    alert('Demo Mode: ข้อความนี้จะถูกส่งใน LINE chat\n\n' + displayText);
    return;
  }

  if (!isInLineApp()) {
    console.warn('[LIFF] Not in LINE app, cannot send message');
    alert('กรุณาเปิดใน LINE app เพื่อส่งข้อความ');
    return;
  }

  try {
    console.log('[LIFF] Sending message via liff.sendMessages...');
    if (typeof message === 'string') {
      await liff.sendMessages([{ type: 'text', text: message }]);
    } else {
      await liff.sendMessages([message as any]);
    }
    console.log('[LIFF] Message sent successfully');
  } catch (error) {
    console.error('[LIFF] Failed to send message:', error);
    alert('ไม่สามารถส่งข้อความได้: ' + (error instanceof Error ? error.message : 'Unknown error'));
    throw error;
  }
}

/**
 * Share target picker (share to friends/groups)
 */
export async function shareMessage(text: string): Promise<void> {
  if (isDemoMode) {
    console.log('[Demo] Would share message:', text);
    return;
  }

  if (!liff.isApiAvailable('shareTargetPicker')) {
    console.log('Share target picker is not available');
    return;
  }

  try {
    await liff.shareTargetPicker([
      {
        type: 'text',
        text,
      },
    ]);
  } catch (error) {
    console.error('Failed to share message:', error);
  }
}

/**
 * Get access token for API calls
 */
export function getAccessToken(): string | null {
  if (isDemoMode) return 'demo-token';
  return liff.getAccessToken();
}

/**
 * Open external URL
 */
export function openUrl(url: string, external: boolean = false): void {
  if (external) {
    liff.openWindow({ url, external: true });
  } else {
    window.location.href = url;
  }
}
